/**
 * Centralized string resources for the application
 * Enables easy maintenance and i18n readiness
 */

export const STRINGS = {
  // App
  APP: {
    TITLE: 'Security Vulnerability Dashboard',
    LOADING: 'Loading vulnerability data...',
    PROCESSING_MESSAGE: 'Processing 371MB of vulnerability data...',
    ERROR_TITLE: 'Error loading data:',
  },

  // Table
  TABLE: {
    TITLE: 'Vulnerabilities',
    SELECTED: 'selected',
    COMPARE: 'Compare',
    CLEAR: 'Clear',
    OF: 'of',
    SELECT_ALL: 'Select all',
    COLUMNS: 'Columns',
    SHOW_COLUMNS: 'Show Columns',
  },

  // Table columns
  COLUMNS: {
    SEVERITY: 'Severity',
    CVE: 'CVE',
    CVSS: 'CVSS',
    PACKAGE: 'Package',
    ANALYSIS_STATUS: 'Analysis Status',
    ATTACK_VECTOR: 'Vector',
    EXPLOIT: 'Exploit',
    FIX: 'Fix',
    RISK_FACTORS: 'Risk Factors',
    GROUP: 'Group',
    IMAGE: 'Image',
  },

  // Filters
  FILTERS: {
    SEARCH_PLACEHOLDER: 'Search CVE, package...',
    HIDE_LABEL: 'Hide:',
    MANUAL: 'Analysis',
    AI: 'AI Analysis',
    MANUAL_PILL: 'Analysis: Hidden',
    AI_PILL: 'AI Analysis: Hidden',
    FILTERS_ACTIVE: 'Filters Active',
    CLEAR_ALL: 'Clear all',
    ADD_FILTER: 'Filter',
  },

  // Filter columns (should match table column headers for consistency)
  FILTER_COLUMNS: {
    CVE: 'CVE',
    PACKAGE_NAME: 'Package',
    SEVERITY: 'Severity',
    CVSS: 'CVSS',
    ANALYSIS_STATUS: 'Analysis Status',
    ATTACK_VECTOR: 'Vector',
    EXPLOIT: 'Exploit',
    FIX: 'Fix',
    GROUP_NAME: 'Group',
    IMAGE_NAME: 'Image',
  },

  // Filter operators
  FILTER_OPERATORS: {
    CONTAINS: 'contains',
    EQUALS: 'equals',
    STARTS_WITH: 'starts with',
    ENDS_WITH: 'ends with',
    NOT_CONTAINS: 'not contains',
    NOT_EQUALS: 'not equals',
    GT: '>',
    GTE: '>=',
    LT: '<',
    LTE: '<=',
    IS: 'is',
    IS_NOT: 'is not',
  },

  // Severity levels
  SEVERITY: {
    CRITICAL: 'Critical',
    HIGH: 'High',
    MEDIUM: 'Medium',
    LOW: 'Low',
  },

  // Attack vector options
  ATTACK_VECTOR: {
    NETWORK: 'Network',
    ADJACENT: 'Adjacent',
    LOCAL: 'Local',
    PHYSICAL: 'Physical',
  },

  // Boolean options
  BOOLEAN: {
    YES: 'Yes',
    NO: 'No',
  },

  // Analysis status
  ANALYSIS_STATUS: {
    VALID: 'Pending Review',
    AI_NO_RISK: 'AI Cleared',
    MANUAL_NO_RISK: 'Manually Cleared',
    PENDING_REVIEW: 'Pending Review',
  },

  // Charts
  CHARTS: {
    VULNERABILITIES_OVER_TIME: 'Vulnerabilities Over Time',
    TOP_RISK_FACTORS: 'Top Risk Indicators',
    SHOWING: 'Showing',
    TOTAL: 'Total',
    VULNERABILITIES: 'Vulnerabilities',
  },

  // Time granularity
  GRANULARITY: {
    YEAR: 'Year',
    MONTH: 'Month',
    WEEK: 'Week',
    DAY: 'Day',
    HOUR: 'Hour',
  },

  // Time range options
  TIME_RANGE: {
    LAST_30_DAYS: 'Last 30 days',
    LAST_90_DAYS: 'Last 90 days',
    LAST_12_MONTHS: 'Last 12 months',
    ALL_TIME: 'All time',
  },

  // Drawer
  DRAWER: {
    CVSS_SCORE: 'CVSS Score',
    ANALYSIS_STATUS: 'Analysis Status',
    PACKAGE: 'Package',
    LOCATION: 'Location',
    RISK_FACTORS: 'Risk Factors',
    NO_RISK_FACTORS: 'No risk factors identified',
    DESCRIPTION: 'Description',
    NO_DESCRIPTION: 'No description available',
    PUBLISHED: 'Published',
    FIX: 'Fix',
    VIEW_ON_NVD: 'View on NVD',
  },

  // Comparison modal
  COMPARISON: {
    TITLE: 'Compare Vulnerabilities',
    HINT: 'Differences are highlighted. Unique risk factors have dashed borders.',
    HIGHEST: '(highest)',
    NONE: 'None',
  },

  // Export
  EXPORT: {
    BUTTON: 'Export',
    CSV: 'Export as CSV',
    JSON: 'Export as JSON',
  },

  // Tooltips
  TOOLTIPS: {
    TOGGLE_COLOR_MODE: 'Toggle color mode',
    VIEW_CVE_DETAILS: 'View CVE details',
    VIEW_ON_NVD: 'View on NVD',
    HIDE_MANUAL: 'Filter out CVEs marked invalid by manual review to focus on unreviewed vulnerabilities',
    HIDE_AI: 'Filter out CVEs marked invalid by AI analysis to focus on unreviewed vulnerabilities',
    ROW_DENSITY: 'Change table row density',
    EXPLOIT_AVAILABLE: 'Exploit available',
    NO_EXPLOIT: 'No known exploit',
    FIX_AVAILABLE: 'Fix available',
    NO_FIX: 'No fix available',
    // Analysis breakdown categories
    ANALYSIS_VALID: 'CVEs not yet analyzed - require review',
    ANALYSIS_AI: 'CVEs that AI determined pose no risk to this environment',
    ANALYSIS_MANUAL: 'CVEs that analysts reviewed and marked as not applicable',
    // Column headers
    CVSS: 'Common Vulnerability Scoring System (0-10). Higher scores indicate more severe vulnerabilities.',
    SEVERITY_CRITICAL: 'Critical (CVSS 9.0-10.0): Immediate action required',
    SEVERITY_HIGH: 'High (CVSS 7.0-8.9): Address as soon as possible',
    SEVERITY_MEDIUM: 'Medium (CVSS 4.0-6.9): Schedule for remediation',
    SEVERITY_LOW: 'Low (CVSS 0.1-3.9): Fix when convenient',
    ATTACK_VECTOR: 'How the vulnerability can be exploited. Network = remotely exploitable without user interaction.',
    // Chart interactions
    CHART_TYPE_BAR: 'Bar chart: Compare discrete time periods',
    CHART_TYPE_AREA: 'Area chart: Visualize trends and cumulative volume',
  },

  // Row density
  ROW_DENSITY: {
    COMPACT: 'Compact',
    NORMAL: 'Normal',
    COMFORTABLE: 'Comfortable',
  },

  // Charts section
  CHARTS_SECTION: {
    SHOW: 'Show Chart',
    HIDE: 'Hide Chart',
  },

  // Filters section
  FILTERS_SECTION: {
    SHOW: 'Show Filters',
    HIDE: 'Hide Filters',
  },

  // Insights section
  INSIGHTS_SECTION: {
    TITLE: 'Additional Insights',
    SHOW: 'Show Insights',
    HIDE: 'Hide Insights',
  },

  // Analysis breakdown
  ANALYSIS_BREAKDOWN: {
    TITLE: 'Analysis Breakdown',
    TOTAL: 'Total Vulnerabilities',
    CARDS_TOOLTIP: 'Summary table showing counts and severity breakdown by status',
    COMPARE_TOOLTIP: 'Compare AI vs Manual review efficiency and severity distribution',
  },

  // Search suggestions
  SEARCH: {
    RECENT_SEARCHES: 'Recent Searches',
    TRY_SEARCHING: 'Try Searching',
    POPULAR_PACKAGES: 'Popular Packages',
    CLEAR_SEARCH: 'Clear search',
    HINTS: {
      CVE: { value: 'CVE-2024-', label: 'Search by CVE ID', example: 'CVE-2024-1234' },
      PACKAGE: { value: 'log4j', label: 'Search by package', example: 'log4j, openssl, spring' },
      DESCRIPTION: { value: 'remote code', label: 'Search in description', example: 'remote code execution' },
    },
  },

  // Filter builder
  FILTER_BUILDER: {
    SELECT_PLACEHOLDER: 'Select...',
    VALUE_PLACEHOLDER: 'Value...',
    TEXT_PLACEHOLDER: 'Search...',
  },

  // Month abbreviations for charts
  MONTHS: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],

  // Misc
  MISC: {
    NA: 'N/A',
    LOADING_CHARTS: 'Loading charts...',
    LOADING_RISK_FACTORS: 'Loading risk factors...',
    FACTOR: 'factor',
    FACTORS: 'factors',
    NO_RESULTS: 'No results found',
  },
} as const

// Type for accessing nested string keys
export type StringKeys = typeof STRINGS
